// ============================================================
// The audience (v8.363.1): who a line is for, as the rules read it.
//
//   table            ['*']
//   players          ['@players', me]        - persistent: late joiners see it
//   channel:<id>     the group's members (+ the GM when they listen) + me
//   dm:<uid>         [me, uid]
//
// The sender is always named, so they can read their own line back.
// Pure.
// ============================================================
import { TO_ALL, TO_PLAYERS, type ChatChannel, type ChatMessage, type ConversationKey } from './types';

export type Target =
  { kind: 'table' } | { kind: 'players' } | { kind: 'channel'; id: string } | { kind: 'dm'; uid: string };

export function targetOf(conv: ConversationKey): Target {
  if (conv === 'table') return { kind: 'table' };
  if (conv === 'players') return { kind: 'players' };
  if (conv.startsWith('channel:')) return { kind: 'channel', id: conv.slice('channel:'.length) };
  return { kind: 'dm', uid: conv.slice('dm:'.length) };
}

export function convOf(target: Target): ConversationKey {
  switch (target.kind) {
    case 'table':
      return 'table';
    case 'players':
      return 'players';
    case 'channel':
      return `channel:${target.id}`;
    case 'dm':
      return `dm:${target.uid}`;
  }
}

export function audienceFor(target: Target, me: string, channels: ChatChannel[], gmUid: string | null): string[] {
  switch (target.kind) {
    case 'table':
      return [TO_ALL];
    case 'players':
      return [TO_PLAYERS, me];
    case 'channel': {
      const ch = channels.find((c) => c.id === target.id);
      const members = ch ? [...ch.members] : [];
      if (ch?.gmListens && gmUid) members.push(gmUid);
      members.push(me);
      return [...new Set(members)];
    }
    case 'dm':
      return [...new Set([me, target.uid])];
  }
}

/** Which conversation a received line belongs to, from where I sit. */
export function conversationOf(msg: ChatMessage, me: string): ConversationKey {
  if (msg.audience.includes(TO_ALL)) return 'table';
  if (msg.audience.includes(TO_PLAYERS)) return 'players';
  if (msg.conv.startsWith('channel:')) return msg.conv;
  const other = msg.audience.find((u) => u !== me) ?? (msg.fromUid !== me ? msg.fromUid : me);
  return `dm:${other}`;
}
